// docs/119 — a hero's Path page: every level from 2 to 20 (what each brought, what the levels to
// come will bring, and any choice left open, which can still be made), the training tracks and
// talents banked skill points buy, and the laurels past 20. Anyone may look; only the owner acts.
// Opened from the roster chip, the hero sheet and straight after taking a level
// (`useOverlay().openHeroPath`); `HeroPathHost` is mounted once in App.
import { useMemo, useState } from 'react';
import { Badge, Button, Card, Eyebrow, Modal, Pips, Tabs, confirmDialog } from '../components';
import { CLASS_NAMES, SPECIES_NAMES } from '../../core/sim/adapt';
import { rpc } from '../../core/net/reducers';
import { useHeroCallings, useHeroProgress, useWorldHeroes } from '../../core/subscriptions/hooks';
import { useSession } from '../../core/stores/session';
import { identityHex } from '../../core/net/connection';
import { useOverlay } from '../overlay';
import { cap, classResource } from '../screens/forge';
import {
  catalogue, featureLines, featureOf, heroWalk, holdsAbility, isChoice, isOpen, linesWords, optionCard, powerWords, stepKind, stepTitle, TRACK_TOP, type SlotView, type Walk,
} from '../../core/sim/progression';
import '../screens/levelup.css';

type Tab = 'levels' | 'training' | 'talents' | 'laurels';

/** Gold a retraining costs a level (fk-world `levels::RETRAIN_GOLD_PER_LEVEL`). */
export const RETRAIN_GOLD_PER_LEVEL = 20;

export function HeroPathHost() {
  const heroId = useOverlay((s) => s.heroPath);
  const close = useOverlay((s) => s.closeHeroPath);
  const worldId = useSession((s) => s.worldId);
  const hero = useWorldHeroes(worldId).find((h) => h.id === heroId);
  return (
    <Modal open={!!hero} onClose={close} eyebrow={hero ? `Level ${hero.level} ${cap(CLASS_NAMES[hero.class] ?? 'fighter')}` : undefined} title={hero ? `${hero.name}'s path` : ''} size="wide" testId="hero-path">
      {hero && <PathBody heroId={hero.id} worldId={hero.worldId} />}
    </Modal>
  );
}

function PathBody({ heroId, worldId }: { heroId: bigint; worldId: bigint }) {
  const h = useWorldHeroes(worldId).find((x) => x.id === heroId)!;
  const calling = useHeroCallings(worldId).find((c) => c.heroId === heroId);
  const row = useHeroProgress(worldId).find((r) => r.heroId === heroId);
  const me = useSession((s) => s.identity ?? '');
  const mine = identityHex(h.owner) === me;
  const [tab, setTab] = useState<Tab>('levels');
  const classId = CLASS_NAMES[h.class] ?? 'fighter';
  const resource = classResource(h.class);
  const walk: Walk | undefined = useMemo(() => {
    try { return heroWalk(h.class, h.species, h.level, calling?.pick ?? 0, calling?.spells ?? [], row); } catch { return undefined; }
  }, [h.class, h.species, h.level, calling, row]);
  if (!walk) return <p>The path could not be read.</p>;
  const open = walk.slots.filter((s) => s.level <= h.level && isOpen(s)).length;
  const retrainable = (row?.training ?? []).some((r) => r > 0) || (row?.talents ?? []).length > 0;
  const retrainCost = RETRAIN_GOLD_PER_LEVEL * h.level;
  const retrain = async () => {
    if (!(await confirmDialog({ title: 'Retrain?', text: `Forget every training rank and talent and bank their ${walk.spent} points again, for ${retrainCost} gold. The choices made at each level stay.`, confirmLabel: `Retrain for ${retrainCost} gold` }))) return;
    await rpc.retrainHero(h.id);
  };
  return (
    <div className="fk-hpath" data-testid="hero-path-body" data-banked={walk.banked}>
      <div className="fk-hpath__head">
        <div className="fk-hpath__who">{cap(classId)} · {cap(SPECIES_NAMES[h.species] ?? 'hero')}{pathName(walk) ? <> · <b>{pathName(walk)}</b></> : null}</div>
        <div className="fk-hpath__points" data-testid="hero-path-points">
          <span className="fk-hpath__banked"><b>{walk.banked}</b> skill point{walk.banked === 1 ? '' : 's'} banked</span>
          <span className="fk-hpath__faint">{walk.earned} earned · {walk.spent} spent</span>
          {mine && retrainable && <Button size="sm" variant="ghost" onClick={() => void retrain()} disabled={h.gold < retrainCost} title={h.gold < retrainCost ? `Retraining costs ${retrainCost} gold` : undefined}>Retrain ({retrainCost} gold)</Button>}
        </div>
      </div>
      <Tabs<Tab> label="Path" value={tab} onChange={setTab} tabs={[
        { id: 'levels', label: 'Levels', badge: open > 0 ? <Badge tone="gold" filled>{open}</Badge> : undefined, testId: 'path-tab-levels' },
        { id: 'training', label: 'Training', testId: 'path-tab-training' },
        { id: 'talents', label: 'Talents', testId: 'path-tab-talents' },
        ...(h.level > TRACK_TOP ? [{ id: 'laurels' as const, label: 'Laurels', testId: 'path-tab-laurels' }] : []),
      ]} />
      {tab === 'levels' && <Levels walk={walk} level={h.level} classId={classId} resource={resource} heroId={h.id} mine={mine} />}
      {tab === 'training' && <TrainingTab walk={walk} heroId={h.id} mine={mine} resource={resource} />}
      {tab === 'talents' && <TalentsTab walk={walk} heroId={h.id} mine={mine} resource={resource} />}
      {tab === 'laurels' && <LaurelsTab laurels={[...(row?.laurels ?? [])]} resource={resource} />}
    </div>
  );
}

function pathName(w: Walk): string | undefined {
  const s = w.slots.find((v) => stepKind(v.step) === 'path');
  return s?.chosen && s.value !== null ? featureOf(s.value)?.name : undefined;
}

/** A slot's value in a word: a feature's or an ability's name, or the points. */
function valueName(s: SlotView, classId: string): string {
  if (s.value === null) return '—';
  if (stepKind(s.step) === 'points') return `+${s.value} skill point${s.value === 1 ? '' : 's'}`;
  return optionCard(s, s.value, classId).name;
}

function Levels({ walk, level, classId, resource, heroId, mine }: { walk: Walk; level: number; classId: string; resource: string; heroId: bigint; mine: boolean }) {
  const [choosing, setChoosing] = useState<string>();
  const [busy, setBusy] = useState(false);
  const byLevel = new Map<number, SlotView[]>();
  for (const s of walk.slots) byLevel.set(s.level, [...(byLevel.get(s.level) ?? []), s]);
  const pick = async (s: SlotView, v: number) => {
    setBusy(true);
    const r = await rpc.pickProgress(heroId, s.level, s.slot, v);
    setBusy(false);
    if (r.ok) setChoosing(undefined);
  };
  return (
    <ol className="fk-hpath__levels" data-testid="path-levels">
      {[...byLevel.entries()].map(([lv, slots]) => {
        const reached = lv <= level;
        return (
          <li key={lv} className={`fk-hpath__level ${reached ? 'fk-hpath__level--reached' : 'fk-hpath__level--ahead'}`} data-level={lv}>
            <span className="fk-hpath__lv" aria-label={`Level ${lv}`}>{lv}</span>
            <div className="fk-hpath__slots">
              {slots.filter((s) => stepKind(s.step) !== 'points' || reached).map((s) => {
                const key = `${s.level}:${s.slot}`;
                const openNow = reached && isOpen(s);
                const f = !holdsAbility(s) && s.value !== null && stepKind(s.step) !== 'points' ? featureOf(s.value) : undefined;
                const tip = f ? [f.blurb, ...featureLines(f, resource)].join('\n') : s.value !== null && holdsAbility(s) ? optionCard(s, s.value, classId, resource).lines.join('\n') : undefined;
                return (
                  <div key={key} className="fk-hpath__slot" data-kind={stepKind(s.step)} data-open={openNow || undefined}>
                    <span className="fk-hpath__slot-kind">{stepTitle(s, classId)}</span>
                    <span className="fk-hpath__slot-value" title={tip}>
                      {valueName(s, classId)}
                      {isChoice(s) && !s.chosen && <Badge tone={reached ? 'amber' : 'grey'}>{reached ? 'default — choose' : 'default'}</Badge>}
                    </span>
                    {openNow && mine && <Button size="sm" variant="ghost" onClick={() => setChoosing(choosing === key ? undefined : key)} data-testid={`path-choose-${s.level}-${s.slot}`}>{choosing === key ? 'Close' : 'Choose'}</Button>}
                    {choosing === key && (
                      <div className="fk-levelup__options fk-levelup__options--many">
                        {s.options.map((id) => {
                          const c = optionCard(s, id, classId, resource);
                          return (
                            <Card key={id} as="button" interactive column className="fk-levelup__card fk-levelup__option" onClick={() => void pick(s, id)} disabled={busy} data-testid={`path-option-${id}`}>
                              <span className="fk-levelup__card-title">{c.name}</span>
                              {c.tag && <span className="fk-levelup__tags"><Badge tone="plum">{c.tag}</Badge></span>}
                              {c.blurb && <span className="fk-levelup__card-text fk-levelup__card-blurb">{c.blurb}</span>}
                              {c.lines.map((l) => <span key={l} className="fk-levelup__card-text">{l}</span>)}
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function TrainingTab({ walk, heroId, mine, resource }: { walk: Walk; heroId: bigint; mine: boolean; resource: string }) {
  const [busy, setBusy] = useState<number>();
  const train = async (id: number) => { setBusy(id); await rpc.trainHero(heroId, id); setBusy(undefined); };
  return (
    <div className="fk-hpath__grid" data-testid="path-training">
      {catalogue().training.map((t) => {
        const v = walk.training.find((x) => x.id === t.id);
        const ranks = v?.ranks ?? 0;
        const next = t.costs[ranks];
        const per = [...linesWords(t.per_rank, resource), ...t.powers.map(powerWords)];
        return (
          <Card key={t.id} column className="fk-hpath__track" data-testid={`train-${t.id}`} data-ranks={ranks}>
            <div className="fk-hpath__track-head"><b>{t.name}</b><Pips value={ranks} max={t.costs.length} label={`${t.name} ranks`} /></div>
            <span className="fk-hpath__faint">{t.blurb}</span>
            <span>Each rank: {per.join('; ')}</span>
            <span className="fk-hpath__faint">{next === undefined ? 'fully trained' : `next rank ${next} point${next === 1 ? '' : 's'}${t.level > 2 ? ` · from level ${t.level}` : ''}`}</span>
            {mine && next !== undefined && (
              <Button size="sm" onClick={() => void train(t.id)} disabled={!!v?.why_not || busy !== undefined} loading={busy === t.id} title={v?.why_not ?? undefined} data-testid={`train-${t.id}-buy`}>Train (−{next})</Button>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function TalentsTab({ walk, heroId, mine, resource }: { walk: Walk; heroId: bigint; mine: boolean; resource: string }) {
  const [busy, setBusy] = useState<number>();
  const learn = async (id: number) => { setBusy(id); await rpc.learnTalent(heroId, id); setBusy(undefined); };
  const groups: [string, number[]][] = [];
  const classIds = new Set(catalogue().classes.flatMap((c) => c.talents.map((t) => t.feature)));
  const speciesIds = new Set(catalogue().species.flatMap((s) => s.talents.map((t) => t.feature)));
  groups.push(['Class', walk.talents.filter((t) => classIds.has(t.feature)).map((t) => t.feature)]);
  groups.push(['Heritage', walk.talents.filter((t) => speciesIds.has(t.feature)).map((t) => t.feature)]);
  groups.push(['Anyone', walk.talents.filter((t) => !classIds.has(t.feature) && !speciesIds.has(t.feature)).map((t) => t.feature)]);
  return (
    <div className="fk-hpath__talents" data-testid="path-talents">
      {groups.filter(([, ids]) => ids.length).map(([name, ids]) => (
        <section key={name}>
          <Eyebrow>{name}</Eyebrow>
          <div className="fk-hpath__grid">
            {ids.map((id) => {
              const t = walk.talents.find((x) => x.feature === id)!;
              const f = featureOf(id);
              return (
                <Card key={id} column className={`fk-hpath__talent ${t.taken ? 'fk-hpath__talent--taken' : ''}`} data-testid={`talent-${id}`} data-taken={t.taken || undefined}>
                  <div className="fk-hpath__track-head"><b>{f?.name ?? id}</b>{t.taken ? <Badge tone="green" filled>learned</Badge> : <Badge tone="gold">{t.cost} pts · L{t.level}</Badge>}</div>
                  {f?.blurb && <span className="fk-hpath__faint">{f.blurb}</span>}
                  {f && featureLines(f, resource).map((l) => <span key={l}>{l}</span>)}
                  {!t.taken && t.why_not && <span className="fk-hpath__why">{t.why_not}</span>}
                  {mine && !t.taken && <Button size="sm" onClick={() => void learn(id)} disabled={!!t.why_not || busy !== undefined} loading={busy === id} data-testid={`talent-${id}-learn`}>Learn (−{t.cost})</Button>}
                </Card>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function LaurelsTab({ laurels, resource }: { laurels: number[]; resource: string }) {
  if (!laurels.length) return <p className="fk-hpath__faint">No laurels yet: every level past 20 brings one.</p>;
  return (
    <ol className="fk-hpath__grid" data-testid="path-laurels">
      {laurels.map((id, i) => {
        const f = featureOf(id);
        return (
          <Card as="li" key={`${id}-${i}`} column className="fk-hpath__talent">
            <div className="fk-hpath__track-head"><b>{f?.name ?? id}</b><Badge tone="gold">L{TRACK_TOP + 1 + i}</Badge></div>
            {f?.blurb && <span className="fk-hpath__faint">{f.blurb}</span>}
            {f && featureLines(f, resource).map((l) => <span key={l}>{l}</span>)}
          </Card>
        );
      })}
    </ol>
  );
}
